import { useEffect, useState } from 'react';

interface Clue {
  value: number;
  clue: string;
  solution: string;
  dailyDouble?: boolean;
}

interface Category {
  category: string;
  clues: Clue[];
}

interface FinalRound {
  category: string;
  clue: string;
  solution: string;
}

interface GameData {
  game: {
    single: Category[];
    double: Category[];
    final: FinalRound;
  };
  players?: any;
  round?: any;
}

interface Props {
  gameData: GameData | null;
  send: (msg: any) => void;
}

type RegularRound = 'single' | 'double';

function defaultClue(): Clue {
  return { value: 100, clue: '', solution: '', dailyDouble: false };
}

function defaultCategory(): Category {
  return {
    category: 'NEW CATEGORY',
    clues: [100, 200, 300, 400, 500].map(v => ({ value: v, clue: '', solution: '' })),
  };
}

export default function AdminJeopardy({ gameData, send }: Props) {
  const [local, setLocal] = useState<GameData | null>(null);
  const [round, setRound] = useState<RegularRound | 'final'>('single');
  const [dirty, setDirty] = useState(false);

  // Initialize / re-initialize when server data arrives. Don't clobber unsaved
  // local edits — if user has dirty changes, leave them alone.
  useEffect(() => {
    if (gameData && !dirty) {
      setLocal(JSON.parse(JSON.stringify(gameData)));
    }
  }, [gameData, dirty]);

  if (!local) {
    return <div className="adm-empty">Loading game data…</div>;
  }

  function patch(updater: (draft: GameData) => void) {
    setLocal(prev => {
      if (!prev) return prev;
      const draft = JSON.parse(JSON.stringify(prev)) as GameData;
      updater(draft);
      return draft;
    });
    setDirty(true);
  }

  function save() {
    if (!local) return;
    send({ type: 'JEOPARDY_REPLACE_GAME', gameData: local });
    setDirty(false);
  }

  function reset() {
    if (!gameData) return;
    setLocal(JSON.parse(JSON.stringify(gameData)));
    setDirty(false);
  }

  // ── Category / clue mutators ─────────────────────────────────────────────
  function setCategoryName(r: RegularRound, ci: number, name: string) {
    patch(d => { d.game[r][ci].category = name; });
  }

  function addCategory(r: RegularRound) {
    patch(d => { d.game[r].push(defaultCategory()); });
  }

  function deleteCategory(r: RegularRound, ci: number) {
    patch(d => { d.game[r].splice(ci, 1); });
  }

  function moveCategory(r: RegularRound, ci: number, dir: -1 | 1) {
    patch(d => {
      const arr = d.game[r];
      const ni = ci + dir;
      if (ni < 0 || ni >= arr.length) return;
      [arr[ci], arr[ni]] = [arr[ni], arr[ci]];
    });
  }

  function setClueField(r: RegularRound, ci: number, qi: number, field: keyof Clue, value: any) {
    patch(d => {
      const clue: any = d.game[r][ci].clues[qi];
      if (field === 'value') {
        clue.value = Number(value) || 0;
      } else {
        clue[field] = value;
      }
    });
  }

  function addClue(r: RegularRound, ci: number) {
    patch(d => { d.game[r][ci].clues.push(defaultClue()); });
  }

  function deleteClue(r: RegularRound, ci: number, qi: number) {
    patch(d => { d.game[r][ci].clues.splice(qi, 1); });
  }

  function moveClue(r: RegularRound, ci: number, qi: number, dir: -1 | 1) {
    patch(d => {
      const arr = d.game[r][ci].clues;
      const ni = qi + dir;
      if (ni < 0 || ni >= arr.length) return;
      [arr[qi], arr[ni]] = [arr[ni], arr[qi]];
    });
  }

  function setFinalField(field: keyof FinalRound, value: string) {
    patch(d => { d.game.final[field] = value; });
  }

  return (
    <div className="adm-jeop">
      <div className="adm-toolbar">
        <div className="adm-jeop-rounds">
          <button
            className={`adm-tab-pill ${round === 'single' ? 'adm-tab-pill-active' : ''}`}
            onClick={() => setRound('single')}
          >
            Single Jeopardy ({local.game.single.length} cats)
          </button>
          <button
            className={`adm-tab-pill ${round === 'double' ? 'adm-tab-pill-active' : ''}`}
            onClick={() => setRound('double')}
          >
            Double Jeopardy ({local.game.double.length} cats)
          </button>
          <button
            className={`adm-tab-pill ${round === 'final' ? 'adm-tab-pill-active' : ''}`}
            onClick={() => setRound('final')}
          >
            Final Jeopardy
          </button>
        </div>

        <div className="adm-jeop-save">
          {dirty && <span className="adm-dirty">● unsaved</span>}
          <button className="adm-btn" onClick={reset} disabled={!dirty}>Discard</button>
          <button className="adm-btn adm-btn-primary" onClick={save} disabled={!dirty}>
            Save Changes
          </button>
        </div>
      </div>

      {round === 'final' ? (
        <FinalEditor final={local.game.final} onChange={setFinalField} />
      ) : (
        <RegularRoundEditor
          categories={local.game[round]}
          onSetCategoryName={(ci, name) => setCategoryName(round, ci, name)}
          onAddCategory={() => addCategory(round)}
          onDeleteCategory={(ci) => deleteCategory(round, ci)}
          onMoveCategory={(ci, dir) => moveCategory(round, ci, dir)}
          onSetClueField={(ci, qi, f, v) => setClueField(round, ci, qi, f, v)}
          onAddClue={(ci) => addClue(round, ci)}
          onDeleteClue={(ci, qi) => deleteClue(round, ci, qi)}
          onMoveClue={(ci, qi, dir) => moveClue(round, ci, qi, dir)}
        />
      )}
    </div>
  );
}

function FinalEditor({
  final,
  onChange,
}: {
  final: FinalRound;
  onChange: (field: keyof FinalRound, value: string) => void;
}) {
  return (
    <div className="adm-jeop-final">
      <div className="adm-section-label">Category</div>
      <input
        className="adm-input"
        value={final.category}
        onChange={e => onChange('category', e.target.value)}
      />
      <div className="adm-section-label">Clue</div>
      <textarea
        className="adm-input"
        rows={4}
        value={final.clue}
        onChange={e => onChange('clue', e.target.value)}
      />
      <div className="adm-section-label">Solution</div>
      <textarea
        className="adm-input"
        rows={2}
        value={final.solution}
        onChange={e => onChange('solution', e.target.value)}
      />
    </div>
  );
}

function RegularRoundEditor({
  categories,
  onSetCategoryName,
  onAddCategory,
  onDeleteCategory,
  onMoveCategory,
  onSetClueField,
  onAddClue,
  onDeleteClue,
  onMoveClue,
}: {
  categories: Category[];
  onSetCategoryName: (ci: number, name: string) => void;
  onAddCategory: () => void;
  onDeleteCategory: (ci: number) => void;
  onMoveCategory: (ci: number, dir: -1 | 1) => void;
  onSetClueField: (ci: number, qi: number, field: keyof Clue, value: any) => void;
  onAddClue: (ci: number) => void;
  onDeleteClue: (ci: number, qi: number) => void;
  onMoveClue: (ci: number, qi: number, dir: -1 | 1) => void;
}) {
  const [deleteCatConfirm, setDeleteCatConfirm] = useState<number | null>(null);

  function confirmDeleteCat(ci: number) {
    if (deleteCatConfirm !== ci) {
      setDeleteCatConfirm(ci);
      setTimeout(() => setDeleteCatConfirm(curr => curr === ci ? null : curr), 3000);
      return;
    }
    setDeleteCatConfirm(null);
    onDeleteCategory(ci);
  }

  return (
    <div className="adm-cats">
      {categories.map((cat, ci) => (
        <div key={ci} className="adm-cat">
          <div className="adm-cat-head">
            <input
              className="adm-input adm-cat-name"
              value={cat.category}
              onChange={e => onSetCategoryName(ci, e.target.value)}
            />
            <div className="adm-row-actions">
              <button className="adm-btn adm-btn-small" onClick={() => onMoveCategory(ci, -1)} disabled={ci === 0}>↑</button>
              <button className="adm-btn adm-btn-small" onClick={() => onMoveCategory(ci, 1)} disabled={ci === categories.length - 1}>↓</button>
              <button
                className={`adm-btn adm-btn-small adm-btn-danger ${deleteCatConfirm === ci ? 'adm-btn-confirming' : ''}`}
                onClick={() => confirmDeleteCat(ci)}
              >
                {deleteCatConfirm === ci ? 'Confirm?' : 'Delete cat'}
              </button>
            </div>
          </div>

          <div className="adm-clues">
            {cat.clues.map((clue, qi) => (
              <div key={qi} className="adm-clue">
                <div className="adm-clue-head">
                  <input
                    type="number"
                    className="adm-input adm-clue-value"
                    value={clue.value}
                    onChange={e => onSetClueField(ci, qi, 'value', e.target.value)}
                  />
                  <label className="adm-clue-dd">
                    <input
                      type="checkbox"
                      checked={!!clue.dailyDouble}
                      onChange={e => onSetClueField(ci, qi, 'dailyDouble', e.target.checked)}
                    />
                    Daily Double ★
                  </label>
                  <div className="adm-row-actions">
                    <button className="adm-btn adm-btn-small" onClick={() => onMoveClue(ci, qi, -1)} disabled={qi === 0}>↑</button>
                    <button className="adm-btn adm-btn-small" onClick={() => onMoveClue(ci, qi, 1)} disabled={qi === cat.clues.length - 1}>↓</button>
                    <button className="adm-btn adm-btn-small adm-btn-danger" onClick={() => onDeleteClue(ci, qi)}>×</button>
                  </div>
                </div>
                <textarea
                  className="adm-input"
                  rows={2}
                  placeholder="Clue text"
                  value={clue.clue}
                  onChange={e => onSetClueField(ci, qi, 'clue', e.target.value)}
                />
                <textarea
                  className="adm-input"
                  rows={1}
                  placeholder='Solution (e.g. "What is Cleveland?")'
                  value={clue.solution}
                  onChange={e => onSetClueField(ci, qi, 'solution', e.target.value)}
                />
              </div>
            ))}
            <button className="adm-btn adm-btn-small" onClick={() => onAddClue(ci)}>+ Add clue</button>
          </div>
        </div>
      ))}
      <button className="adm-btn adm-btn-primary" onClick={onAddCategory}>+ Add category</button>
    </div>
  );
}
